import React, { useRef, useEffect, useState } from "react";

import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

import {
  Wrapper,
  InputWrapper,
  ButtonWrapper,
  PasswordToggleButton,
  FormHeader,
  FormTitle,
  FormSubtitle,
  Divider,
  GoogleButton,
} from "./styles";
import Input from "../../../common/CustomInput";
import { Button } from "../../../../design-system";
import firebase from "firebase/app";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

type Props = {
  setSnackbarError: Function;
  loginUser: (user: any) => void;
  onGoogleSignIn: () => Promise<void>;
};

const LoginForm = ({ setSnackbarError, loginUser, onGoogleSignIn }: Props) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const emailInputRef = useRef<any>(null);

  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  const handleLogin = (): void => {
    setIsLoading(true);
    firebase
      .auth()
      .signInWithEmailAndPassword(email.trim(), password.trim())
      .then((user) => {
        loginUser(user);
      })
      .catch((error) => {
        setIsLoading(false);
        setSnackbarError(error.message);
      });
  };

  const handleGoogleSignIn = (): void => {
    setIsGoogleLoading(true);
    onGoogleSignIn().catch((error) => {
      setIsGoogleLoading(false);
      setSnackbarError(error.message);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" && password.length >= 6 && !isLoading) {
      handleLogin();
    }
  };

  const busy = isLoading || isGoogleLoading;

  return (
    <Wrapper onKeyDown={handleKeyDown}>
      <FormHeader>
        <FormTitle>Welcome back</FormTitle>
        <FormSubtitle>Sign in to your account to continue</FormSubtitle>
      </FormHeader>
      <InputWrapper>
        <Input
          inputRef={(input) => {
            emailInputRef.current = input;
          }}
          onChange={(event: any): void => setEmail(event.target.value)}
          onBlur={() => {}}
          value={email}
          label="Email"
          placeholder="you@example.com"
          id="username"
          type="email"
          error=""
        />
      </InputWrapper>
      <InputWrapper>
        <Input
          onChange={(event: any): void => setPassword(event.target.value)}
          onBlur={() => {}}
          value={password}
          type={showPassword ? "text" : "password"}
          placeholder="••••••••"
          label="Password"
          id="password"
          error=""
        />
        <PasswordToggleButton
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          tabIndex={-1}
        >
          {showPassword ? (
            <VisibilityOff fontSize="small" />
          ) : (
            <Visibility fontSize="small" />
          )}
        </PasswordToggleButton>
      </InputWrapper>
      <ButtonWrapper>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={password.length < 6 || busy}
          isLoading={isLoading}
          onClick={handleLogin}
        >
          Sign in
        </Button>
      </ButtonWrapper>
      <Divider>or</Divider>
      <GoogleButton onClick={handleGoogleSignIn} disabled={busy} type="button">
        <GoogleIcon />
        {isGoogleLoading ? "Signing in…" : "Continue with Google"}
      </GoogleButton>
    </Wrapper>
  );
};

export default LoginForm;
